import { ReactNode } from "react";
import { type LucideIcon } from "lucide-react";
import { StoreApi, UseBoundStore } from "zustand";

import { NodeStore } from "@/components/@types";
import { Icon } from "@/components/atoms/Icon";
import { NodeTitleLayout } from "@/components/organisms/Node/NodeTitleLayout";
import { ObjectActionType } from "@/objects/@types";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";

type SettingsViewLayoutProps = {
  title: string;
  store?: UseBoundStore<StoreApi<NodeStore>> | null;
  isLoading?: boolean;
  actions?: ObjectActionType[];
  /** The page's own buttons, at the right of its title bar. */
  endDecorator?: ReactNode;
  children: ReactNode;
};

export function SettingsViewLayout({
  title,
  store = null,
  isLoading = false,
  actions,
  endDecorator,
  children,
}: SettingsViewLayoutProps) {
  return (
    <div className="flex flex-col h-full w-full min-h-0 overflow-hidden">
      <div className="shrink-0 [border-bottom-width:1px] [border-bottom-style:solid] border-border w-full">
        <NodeTitleLayout
          store={store}
          title={title}
          isLoading={isLoading}
          actions={actions}
          endDecorator={endDecorator}
          // The bars' own 8px in from either side.
          className="pl-2 pr-2"
        />
      </div>
      <div className="flex-1 min-h-0 overflow-auto w-full flex flex-col">
        {children}
      </div>
    </div>
  );
}

type SettingsViewCardProps = {
  /** Drawn 16px wide, the width of the icon or control each row starts with. */
  icon: LucideIcon;
  title: ReactNode;
  /** A line under the title: what it is for, or where it stands. */
  description?: ReactNode;
  /** Its own buttons, at the right of its header. */
  actions?: ReactNode;
  isLoading?: boolean;
  /** Its rows, under its header: each a 16px icon or control, then its words. */
  children?: ReactNode;
  "data-testid"?: string;
};

/**
 * One thing on a settings page - a mailbox, the bank feeds, the members - as
 * a card on the surface tables are drawn on: a header naming it, with its
 * buttons to the right, over a row for each of its parts.
 */
export function SettingsViewCard({
  icon,
  title,
  description,
  actions,
  isLoading = false,
  children,
  "data-testid": dataTestid,
}: SettingsViewCardProps) {
  return (
    <div
      data-testid={dataTestid}
      className="[border-width:1px] [border-style:solid] border-border rounded-lg shadow-xs bg-background overflow-hidden"
    >
      <div className="flex items-center gap-3 pl-3 pr-3 pt-2 pb-2">
        <Skeleton
          loading={isLoading}
          variant="rectangular"
          className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
        >
          <div className="flex text-muted-foreground/80">
            <Icon ReactIcon={icon} size="md" />
          </div>
        </Skeleton>
        <div className="flex-1 min-w-0">
          <Typography level="body-md" className="truncate font-medium">
            <Skeleton loading={isLoading} variant="inline">
              {title}
            </Skeleton>
          </Typography>
          {description && (
            <Typography level="body-sm" className="text-muted-foreground/80">
              <Skeleton loading={isLoading} variant="inline">
                {description}
              </Skeleton>
            </Typography>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-2 shrink-0">{actions}</div>
        )}
      </div>
      {children}
    </div>
  );
}
